import { apiFetch } from "./client";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "./stocky_public_ids";

export interface PurchaseOrderLine {
  variant_id: StockyPublicBigIntId;
  product_id: StockyPublicBigIntId;
  shopify_variant_id: StockyPublicBigIntId;
  sku: string | null;
  /** Shopify 純正 PO 取込の照合キー第2候補（SKU 未設定でも JAN で照合できる）。 */
  barcode?: string | null;
  title: string | null;
  /** 発注時点の税率（店の税率行）。名前と率も持ち歩く＝行が消えても金額を再現できる。 */
  tax_rate_id: StockyPublicBigIntId | null;
  tax_rate_name: string | null;
  tax_rate: string | null;
  available: number;
  /** Shopify の入荷予定（既発注ぶん）。発注点から available と共に差し引く。 */
  incoming: number;
  /** アプリ内で「注文済み」にした未入荷数量（incoming と同列に差し引き済み）。 */
  ordered_open?: number;
  /**
   * 予測由来の値は手動注文書（Free・auto_quantity=false）では null で伏せられる。
   * reorder_point/recommended_qty/line_amount は自動提案を逆算されないよう空欄化される。
   */
  reorder_point: number | null;
  /** 補充目標 S（(s, S) 方策）。補充日数 未設定なら reorder_point と同値。 */
  replenishment_target: number | null;
  recommended_qty: number | null;
  unit_price: string;
  line_amount: string | null;
  /** 原価未設定で販売価格を暫定使用した明細は true（仕入単価は目安）。 */
  is_estimated: boolean;
  /** 仕入単価がマーチャント手入力(cost_override)由来なら true（「手動」バッジ用）。 */
  is_manual_cost: boolean;
  /**
   * 仕入単価が既定仕入先の条件（VariantSupplier.unit_cost）由来なら true。
   * 🔴 この状態では単価欄の手入力（cost_override）は一度も読まれない＝入力させると
   * 「保存は成功したのに表示が戻る」になるので、UI は欄を無効化して理由を出す。
   * optional なのはデプロイスキュー対策。
   */
  is_supplier_cost?: boolean;
  /** 数量を引き上げた MOQ（効いた行のみ・auto のみ・旧 API では欠ける）。 */
  applied_moq?: number | null;
  /** 数量を切り上げた入数（効いた行のみ・auto のみ）。 */
  applied_pack_size?: number | null;
  /**
   * 発注点が未設定（または無効）の明細。数量0で並ぶ点は「在庫十分で発注不要」と
   * 同じだが意味は逆＝**アプリが発注要否を判断できていない**。取り違えると
   * 「発注しなくてよい」と読まれるので UI で明示的に区別する。
   *
   * 🔴 optional なのは互換のため（`assigned_total` と同じ理由＝別々にデプロイされる）。
   */
  is_unmanaged?: boolean;
  /** Pro以上では直近売上に基づくABCランク。販売実績なし／対象外プランは null。 */
  abc_rank?: "A" | "B" | "C" | null;
}

export interface TaxGroup {
  tax_rate_id: StockyPublicBigIntId | null;
  /** マーチャントが設定した税率名（"標準 10%" / "VAT 20%"）＝訳さない。 */
  name: string;
  /** "0.1000" 形式。表示は name を使い、これは再計算用。 */
  rate: string;
  subtotal: string;
  tax: string;
}

export interface PurchaseOrderDraft {
  supplier_id: StockyPublicBigIntId;
  supplier_name: string;
  invoice_registration_no: string | null;
  lines: PurchaseOrderLine[];
  /** 金額の表示通貨（店の通貨）。数量未定でも通貨は決まるので常に入る。 */
  currency: string;
  /** 数量が未定の手動注文書（auto_quantity=false）では税集計は算出できず null。 */
  /** 税率ごとの小計・税額。**その発注書で実際に使われた税率だけ**が並ぶ。 */
  tax_groups: TaxGroup[];
  total_amount: string | null;
  has_estimated_lines: boolean;
}

export interface PurchaseOrderDraftsPage {
  drafts: PurchaseOrderDraft[];
  total: number;
  limit: number;
  offset: number;
  /**
   * false = Free 手動注文書（数量空欄・自動提案なし。マーチャントが数量を手入力）。
   * true  = Starter+ 自動注文書（販売速度から発注数量を自動提案）。
   */
  auto_quantity: boolean;
  /** 明示承認済みの商品へ実際に適用した季節補正。未判断・全除外・通常期は null。 */
  seasonal_adjustment: {
    name: string;
    multiplier: number;
    applied_product_count: number;
  } | null;
  /**
   * プレビューできる季節イベント＝**店の国の暦**（日本＝お中元/お歳暮、米国＝
   * クリスマス商戦/BF …）。カタログの無い国では空配列。
   * 🔴 一覧をこちらに持たない＝国ごとに違うので毎回サーバから受け取る。
   */
  season_previews: SeasonalPreviewOption[];
  season_preview?: SeasonPreview;
  /**
   * 税率の選択肢＝**その店の設定**。🔴 一覧を SPA に持たせない（国ごと・店ごとに
   * 違ううえ増減する）。季節プレビューと同じく毎回サーバから受け取る。
   */
  tax_rates: TaxRateOption[];
}

export interface TaxRateOption {
  id: StockyPublicBigIntId;
  /** マーチャントが設定した名前（"標準 10%" / "VAT 20%"）＝訳さない。 */
  name: string;
  rate: string;
  is_default: boolean;
}

export interface SeasonalPreviewOption {
  /** backend の季節カタログの slug。`season_preview` に渡す値。 */
  slug: string;
  /** 表示名（backend が管理画面の表示言語へ解決済み）。 */
  name: string;
  multiplier: number;
}

/**
 * 季節プレビューの選択値。`"normal"`（通常期＝どの国でも選べる）か、
 * `season_previews[].slug`（店の国の暦）。null = 実際の日付で計算。
 * 🔴 backend は知らない slug を 422 で弾く＝国をまたいだ値は送れない。
 */
export type SeasonPreview = string | null;

export const PURCHASE_ORDER_PAGE_LIMIT = 50;

function canonicalNullableId(value: unknown): string | null {
  return value === null ? null : canonicalStockyPublicBigIntId(value);
}

function normalizePurchaseOrderLine(
  line: PurchaseOrderLine,
): PurchaseOrderLine {
  return {
    ...line,
    variant_id: canonicalStockyPublicBigIntId(line.variant_id),
    product_id: canonicalStockyPublicBigIntId(line.product_id),
    shopify_variant_id: canonicalStockyPublicBigIntId(line.shopify_variant_id),
    tax_rate_id: canonicalNullableId(line.tax_rate_id),
  };
}

function normalizePurchaseOrderPage(
  page: PurchaseOrderDraftsPage,
): PurchaseOrderDraftsPage {
  return {
    ...page,
    drafts: page.drafts.map((draft) => ({
      ...draft,
      supplier_id: canonicalStockyPublicBigIntId(draft.supplier_id),
      lines: draft.lines.map(normalizePurchaseOrderLine),
      tax_groups: draft.tax_groups.map((group) => ({
        ...group,
        tax_rate_id: canonicalNullableId(group.tax_rate_id),
      })),
    })),
    tax_rates: page.tax_rates.map((rate) => ({
      ...rate,
      id: canonicalStockyPublicBigIntId(rate.id),
    })),
  };
}

export async function listPurchaseOrderDrafts(
  limit: number = PURCHASE_ORDER_PAGE_LIMIT,
  offset = 0,
  /** 配送先（ロケーション）別ドラフト。null=未選択（バックエンドが主拠点へ解決）。 */
  locationId: StockyPublicBigIntId | null = null,
  seasonPreview: SeasonPreview = null,
): Promise<PurchaseOrderDraftsPage> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (locationId !== null) {
    params.set("location_id", canonicalStockyPublicBigIntId(locationId));
  }
  if (seasonPreview !== null) params.set("season_preview", seasonPreview);
  const page = await apiFetch<PurchaseOrderDraftsPage>(
    `/api/purchase-orders/drafts?${params.toString()}`,
  );
  return normalizePurchaseOrderPage(page);
}

/** 注文書へ任意追加できる、同期済みの商品バリエーション。 */
export async function listOrderableVariants(
  query = "",
  locationId: StockyPublicBigIntId | null = null,
): Promise<PurchaseOrderLine[]> {
  const lines = await apiFetch<PurchaseOrderLine[]>(
    "/api/purchase-orders/variants",
    {
      query: {
        q: query.trim() || undefined,
        location_id:
          locationId === null
            ? undefined
            : canonicalStockyPublicBigIntId(locationId),
        limit: 200,
      },
    },
  );
  return lines.map(normalizePurchaseOrderLine);
}

/**
 * 貼り付けからの一括追加＝一度に突合できるコード数
 * （backend `purchase_orders.MAX_LOOKUP_CODES` の写し）。
 * 🔴 超過をサーバーは 422 で弾く（黙って切らない）＝画面はここで先に数えて案内を出す。
 */
export const PASTE_LOOKUP_CODE_LIMIT = 200;

/**
 * 貼り付けたセル値（SKU/JAN 候補）をまとめて突合する。突合できた商品だけが
 * `PurchaseOrderLine` で返る＝見つからなかったコードは応答に現れない
 * （呼び出し側が「突合できなかった行」として件数と一覧を見せる）。
 * 🔴 解決規則はスキャンと同じ barcode 優先（サーバー側 `lookup_variants_by_codes`）。
 */
export async function lookupOrderableVariantsByCodes(
  codes: string[],
  locationId: StockyPublicBigIntId | null = null,
): Promise<PurchaseOrderLine[]> {
  // apiFetch の query はスカラー専用＝FastAPI の list Query が要求する
  // `codes=A&codes=B` の繰り返し形式をここで組み立てる。
  const params = new URLSearchParams();
  for (const code of codes) params.append("codes", code);
  if (locationId !== null) {
    params.set("location_id", canonicalStockyPublicBigIntId(locationId));
  }
  const lines = await apiFetch<PurchaseOrderLine[]>(
    `/api/purchase-orders/variants?${params.toString()}`,
  );
  return lines.map(normalizePurchaseOrderLine);
}

export interface SupplierOption {
  id: StockyPublicBigIntId;
  name: string;
  invoice_registration_no: string | null;
  contact_email?: string | null;
  /** 既定仕入先としての割当商品数（仕入先ページの列）。旧 backend では欠ける。 */
  assigned_variant_count?: number;
}

export interface StockySupplierNote {
  revision_id: StockyPublicBigIntId;
  kind: "supplier_private_note" | "supplier_po_note_template";
  text: string;
  revision: number;
  created_at: string;
}

export interface StockySupplierNotes {
  supplier_id: StockyPublicBigIntId;
  supplier_private_note: StockySupplierNote | null;
  supplier_po_note_template: StockySupplierNote | null;
}

export async function listSuppliers(): Promise<SupplierOption[]> {
  const suppliers = await apiFetch<SupplierOption[]>(
    "/api/purchase-orders/suppliers",
  );
  return suppliers.map((supplier) => ({
    ...supplier,
    id: canonicalStockyPublicBigIntId(supplier.id),
  }));
}

/**
 * Read the latest append-only Stocky note revisions for one owned supplier.
 * The private note is internal-only; callers must never copy it into a PO,
 * document, email, or other outbound payload.
 */
export async function getSupplierStockyNotes(
  supplierId: StockyPublicBigIntId,
): Promise<StockySupplierNotes> {
  const canonicalSupplierId = canonicalStockyPublicBigIntId(supplierId);
  const notes = await apiFetch<{
    supplier_id: unknown;
    supplier_private_note: unknown;
    supplier_po_note_template: unknown;
  }>(`/api/purchase-orders/suppliers/${canonicalSupplierId}/stocky-notes`);
  if (
    canonicalStockyPublicBigIntId(notes.supplier_id) !== canonicalSupplierId
  ) {
    throw new TypeError("invalid Stocky note supplier id");
  }

  const normalizeNote = (value: unknown): StockySupplierNote | null => {
    if (value === null) return null;
    if (typeof value !== "object" || value === null) {
      throw new TypeError("invalid Stocky supplier note");
    }
    const row = value as Record<string, unknown>;
    if (
      (row.kind !== "supplier_private_note" &&
        row.kind !== "supplier_po_note_template") ||
      typeof row.text !== "string" ||
      !Number.isSafeInteger(row.revision) ||
      (row.revision as number) < 1 ||
      typeof row.created_at !== "string"
    ) {
      throw new TypeError("invalid Stocky supplier note");
    }
    return {
      revision_id: canonicalStockyPublicBigIntId(row.revision_id),
      kind: row.kind,
      text: row.text,
      revision: row.revision as number,
      created_at: row.created_at,
    };
  };

  return {
    supplier_id: canonicalSupplierId,
    supplier_private_note: normalizeNote(notes.supplier_private_note),
    supplier_po_note_template: normalizeNote(notes.supplier_po_note_template),
  };
}

export interface SupplierCreateInput {
  name: string;
  invoice_registration_no?: string | null;
  contact_email?: string | null;
}

/**
 * 仕入先を手動で作る。Stocky 未使用店（および EOL 後の全新規店）の唯一の投入経路。
 * 名前の重複・空欄は backend が 400 + 日本語 detail を返す（ApiError.detail を表示する）。
 */
export async function createSupplier(
  input: SupplierCreateInput,
): Promise<SupplierOption> {
  const supplier = await apiFetch<SupplierOption>(
    "/api/purchase-orders/suppliers",
    {
      method: "POST",
      body: input,
    },
  );
  return { ...supplier, id: canonicalStockyPublicBigIntId(supplier.id) };
}

export interface UnassignedVariant {
  variant_id: StockyPublicBigIntId;
  sku: string | null;
  title: string | null;
  /** Optional while a newer SPA can still briefly talk to the previous backend. */
  product_title?: string | null;
  /** Server-derived merchant display name; optional during SPA-first rollout. */
  display_title?: string | null;
}

export interface UnassignedVariantsPage {
  variants: UnassignedVariant[];
  /** 絞り込み後の全件数（ページ内件数ではない）。 */
  total: number;
  limit: number;
  offset: number;
  /**
   * 仕入先が割当済みの商品数（店全体・検索語では絞られない）。
   *
   * 🔴 optional なのは互換のため＝SPA と backend は別々にデプロイされるので、
   * この列を返さない版の backend に新しい SPA が当たる時間帯がある。
   */
  assigned_total?: number;
  /**
   * 発注点のカバレッジ（母集団＝仕入先が割当済みの商品）。同じく店全体の値。
   *
   * 🔴 ホームのチェックリストがこれを使うのは、発注点ルーターが Starter+ ゲートで
   * Free では 403 になるため＝非ゲートのこのエンドポイントに信号を寄せている。
   * 発注点の**数**ではなくカバレッジで見るのは、仕入先が未割当の商品にだけ
   * 入れた発注点は注文書に効かないため（`assigned_total` と同じ型の判定）。
   */
  policy_covered_total?: number;
  policy_uncovered_total?: number;
}

export const UNASSIGNED_PAGE_LIMIT = 20;

/**
 * 仕入先が未割当の商品。割当しないと注文書ドラフトに出てこないが、割当UIは
 * ドラフト表の中にしか無いため、未割当側はここから引く必要がある。
 */
export async function listUnassignedVariants(
  query = "",
  limit: number = UNASSIGNED_PAGE_LIMIT,
  offset = 0,
): Promise<UnassignedVariantsPage> {
  const page = await apiFetch<UnassignedVariantsPage>(
    "/api/purchase-orders/unassigned-variants",
    { query: { q: query.trim() || undefined, limit, offset } },
  );
  return {
    ...page,
    variants: page.variants.map((variant) => ({
      ...variant,
      variant_id: canonicalStockyPublicBigIntId(variant.variant_id),
    })),
  };
}

export async function setSupplierInvoiceNo(
  supplierId: StockyPublicBigIntId,
  invoiceRegistrationNo: string | null,
): Promise<{
  supplier_id: StockyPublicBigIntId;
  invoice_registration_no: string | null;
}> {
  const supplier = await apiFetch<{
    supplier_id: StockyPublicBigIntId;
    invoice_registration_no: string | null;
  }>(
    `/api/purchase-orders/suppliers/${canonicalStockyPublicBigIntId(supplierId)}`,
    {
      method: "PATCH",
      body: { invoice_registration_no: invoiceRegistrationNo },
    },
  );
  return {
    ...supplier,
    supplier_id: canonicalStockyPublicBigIntId(supplier.supplier_id),
  };
}

export async function setSupplierContactEmail(
  supplierId: StockyPublicBigIntId,
  contactEmail: string | null,
): Promise<{
  supplier_id: StockyPublicBigIntId;
  contact_email: string | null;
}> {
  const supplier = await apiFetch<{
    supplier_id: StockyPublicBigIntId;
    contact_email: string | null;
  }>(
    `/api/purchase-orders/suppliers/${canonicalStockyPublicBigIntId(supplierId)}`,
    {
      method: "PATCH",
      body: { contact_email: contactEmail },
    },
  );
  return {
    ...supplier,
    supplier_id: canonicalStockyPublicBigIntId(supplier.supplier_id),
  };
}

export interface PurchaseOrderEmailPreview {
  draft_sha: string;
  supplier_id: StockyPublicBigIntId;
  /** Resolved snapshot; requested null still means "current primary". */
  location_id: StockyPublicBigIntId | null;
  input_epoch: number;
  from_email: string;
  to_email: string;
  subject: string;
  text: string;
  /** Exact outbound-safe Stocky PO note confirmed in this preview. */
  purchase_order_note?: string | null;
  /** Optional merchant-selected files, persisted with the same send attempt. */
  attachments?: PurchaseOrderEmailAttachment[];
  /** The server renders this default attachment only when the merchant keeps it selected. */
  include_purchase_order_pdf?: boolean;
}

export interface PurchaseOrderEmailAttachment {
  filename: string;
  content_type: string;
  /** Base64 without a data-URL prefix; no client-side content truncation. */
  content: string;
}

export const PURCHASE_ORDER_EMAIL_ATTACHMENT_LIMITS = {
  maxCount: 3,
  maxBytesPerFile: 1 * 1024 * 1024,
  maxTotalBytes: 2 * 1024 * 1024,
} as const;

export const PURCHASE_ORDER_EMAIL_ATTACHMENT_TYPES: Record<string, string> = {
  ".pdf": "application/pdf",
  ".csv": "text/csv",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
};

export interface PurchaseOrderEmailSendResult {
  sent: boolean;
  replayed: boolean;
  to_email: string;
  supplier_id: StockyPublicBigIntId;
  delivery_id: StockyPublicBigIntId;
  status: string;
  duplicate_suppressed?: boolean;
  replayed_from_delivery_id?: StockyPublicBigIntId | null;
  new_order_after_delivery_id?: StockyPublicBigIntId | null;
  prior_delivered_at?: string | null;
  confirmation_token?: string | null;
}

export interface PurchaseOrderEmailDeliveryOverride {
  priorDeliveryId: StockyPublicBigIntId;
  confirmationToken: "send_new_purchase_order";
}

/** Build, but do not accept or send, the exact server-owned provider body. */
export async function getPurchaseOrderEmailPreview(
  supplierId: StockyPublicBigIntId,
  locationId: StockyPublicBigIntId | null = null,
): Promise<PurchaseOrderEmailPreview> {
  const preview = await apiFetch<PurchaseOrderEmailPreview>(
    `/api/purchase-orders/suppliers/${encodeURIComponent(
      canonicalStockyPublicBigIntId(supplierId),
    )}/email-draft`,
    {
      query: {
        location_id:
          locationId === null
            ? undefined
            : canonicalStockyPublicBigIntId(locationId),
      },
    },
  );
  return {
    ...preview,
    supplier_id: canonicalStockyPublicBigIntId(preview.supplier_id),
    location_id: canonicalNullableId(preview.location_id),
  };
}

export async function emailPurchaseOrderDraft(
  supplierId: StockyPublicBigIntId,
  locationId: StockyPublicBigIntId | null,
  draftSha: string,
  idempotencyKey: string,
  purchaseOrderNote: string | null,
  deliveryOverride: PurchaseOrderEmailDeliveryOverride | null = null,
  emailSnapshot: Pick<
    PurchaseOrderEmailPreview,
    "to_email" | "subject" | "text" | "attachments" | "include_purchase_order_pdf"
  > = { to_email: "", subject: "", text: "" },
): Promise<PurchaseOrderEmailSendResult> {
  const result = await apiFetch<PurchaseOrderEmailSendResult>(
    `/api/purchase-orders/suppliers/${encodeURIComponent(
      canonicalStockyPublicBigIntId(supplierId),
    )}/email`,
    {
      method: "POST",
      query: {
        location_id:
          locationId === null
            ? undefined
            : canonicalStockyPublicBigIntId(locationId),
      },
      headers: {
        "Idempotency-Key": idempotencyKey,
        "If-Match": `"${draftSha}"`,
      },
      body: {
        draft_sha: draftSha,
        email_snapshot: {
          to_email: emailSnapshot.to_email,
          subject: emailSnapshot.subject,
          text: emailSnapshot.text,
          attachments: emailSnapshot.attachments ?? [],
          include_purchase_order_pdf:
            emailSnapshot.include_purchase_order_pdf ?? true,
        },
        purchase_order_note: purchaseOrderNote,
        ...(deliveryOverride === null
          ? {}
          : {
              prior_delivery_id: canonicalStockyPublicBigIntId(
                deliveryOverride.priorDeliveryId,
              ),
              confirmation_token: deliveryOverride.confirmationToken,
            }),
      },
    },
  );
  return {
    ...result,
    supplier_id: canonicalStockyPublicBigIntId(result.supplier_id),
    delivery_id: canonicalStockyPublicBigIntId(result.delivery_id),
    replayed_from_delivery_id: canonicalNullableId(
      result.replayed_from_delivery_id ?? null,
    ),
    new_order_after_delivery_id: canonicalNullableId(
      result.new_order_after_delivery_id ?? null,
    ),
  };
}

export async function assignVariantSupplier(
  variantId: StockyPublicBigIntId,
  supplierId: StockyPublicBigIntId | null,
): Promise<{
  variant_id: StockyPublicBigIntId;
  supplier_id: StockyPublicBigIntId | null;
}> {
  const assignment = await apiFetch<{
    variant_id: StockyPublicBigIntId;
    supplier_id: StockyPublicBigIntId | null;
  }>(
    `/api/purchase-orders/variants/${canonicalStockyPublicBigIntId(variantId)}/supplier`,
    {
      method: "PATCH",
      body: {
        supplier_id:
          supplierId === null
            ? null
            : canonicalStockyPublicBigIntId(supplierId),
      },
    },
  );
  return {
    variant_id: canonicalStockyPublicBigIntId(assignment.variant_id),
    supplier_id: canonicalNullableId(assignment.supplier_id),
  };
}

/**
 * 仕入先を複数の商品にまとめて割り当てる。
 *
 * Stocky 未使用店は全SKUが未割当で始まる（移行組は移行時に PO 履歴から自動で
 * 張られる）ため、1件ずつでは数百SKUの店が最初の注文書に辿り着けない。
 * 他店の商品が混ざっていれば backend が全体を拒否する（部分適用しない）。
 */
export async function assignVariantsSupplier(
  variantIds: StockyPublicBigIntId[],
  supplierId: StockyPublicBigIntId | null,
): Promise<{ updated: number; supplier_id: StockyPublicBigIntId | null }> {
  const assignment = await apiFetch<{
    updated: number;
    supplier_id: StockyPublicBigIntId | null;
  }>("/api/purchase-orders/variants/supplier", {
    method: "PATCH",
    body: {
      variant_ids: variantIds.map(canonicalStockyPublicBigIntId),
      supplier_id:
        supplierId === null ? null : canonicalStockyPublicBigIntId(supplierId),
    },
  });
  return {
    ...assignment,
    supplier_id: canonicalNullableId(assignment.supplier_id),
  };
}

export async function setProductTaxCategory(
  productId: StockyPublicBigIntId,
  taxRateId: StockyPublicBigIntId | null,
): Promise<{
  product_id: StockyPublicBigIntId;
  tax_rate_id: StockyPublicBigIntId | null;
}> {
  const product = await apiFetch<{
    product_id: StockyPublicBigIntId;
    tax_rate_id: StockyPublicBigIntId | null;
  }>(
    `/api/purchase-orders/products/${canonicalStockyPublicBigIntId(productId)}/tax-category`,
    {
      method: "PATCH",
      body: {
        tax_rate_id:
          taxRateId === null ? null : canonicalStockyPublicBigIntId(taxRateId),
      },
    },
  );
  return {
    product_id: canonicalStockyPublicBigIntId(product.product_id),
    tax_rate_id: canonicalNullableId(product.tax_rate_id),
  };
}

/**
 * 仕入単価の手入力値(cost_override)を設定/クリアする。null でクリアし
 * cost/price のデフォルトへ戻す。Shopify 同期では上書きされない永続値。
 */
export async function setVariantCost(
  variantId: StockyPublicBigIntId,
  costOverride: number | null,
): Promise<{
  variant_id: StockyPublicBigIntId;
  cost_override: string | null;
}> {
  const variant = await apiFetch<{
    variant_id: StockyPublicBigIntId;
    cost_override: string | null;
  }>(
    `/api/purchase-orders/variants/${canonicalStockyPublicBigIntId(variantId)}/cost`,
    {
      method: "PATCH",
      body: { cost_override: costOverride },
    },
  );
  return {
    ...variant,
    variant_id: canonicalStockyPublicBigIntId(variant.variant_id),
  };
}

/**
 * 複数商品の仕入単価(cost_override)を1リクエストでまとめて設定/クリアする。
 * 期首の一斉改定や Stocky からの原価一括移行で、行ごとに叩かず1回で保存する経路。
 * 各 costOverride は null でクリア。backend が他店・不明・重複 variant を全体拒否する。
 */
export function setVariantCosts(
  items: {
    variantId: StockyPublicBigIntId;
    costOverride: number | null;
  }[],
): Promise<{ updated: number }> {
  return apiFetch("/api/purchase-orders/variants/cost", {
    method: "PATCH",
    body: {
      items: items.map((i) => ({
        variant_id: canonicalStockyPublicBigIntId(i.variantId),
        cost_override: i.costOverride,
      })),
    },
  });
}
